import type { BadgeState, BenefitState, UserProgressResponse } from "@/types/progression"
import { BENEFIT_ICONS } from "@/constants/progressionActions"
import type { ChecklistAction } from "@/components/progression/ProgressActionChecklist.vue"
import {
  actionDescription,
  benefitStatusLabel,
  formatBadgeDescription,
  formatBadgeName,
  formatBenefitName,
  readableAction,
} from "@/utils/progressionI18n"
import type { TranslateFn } from "@/utils/progressionI18n"

export type ImpactedBenefitView = {
  slug: string
  title: string
  progress: number
  unlocked: boolean
  icon: string
  statusLabel: string
}

export type BadgeActionLike = {
  key: string
  completed?: boolean
  occurrence_count?: number
  max_occurrences?: number
}

export function buildBadgeTitle(
  t: TranslateFn,
  badgeSlug: string,
  progressData: UserProgressResponse | null
): string {
  const catalog = progressData?.catalog.badges[badgeSlug]
  return formatBadgeName(t, badgeSlug, catalog?.title)
}

export function buildBadgeDescription(
  t: TranslateFn,
  badgeSlug: string,
  progressData: UserProgressResponse | null
): string {
  const catalog = progressData?.catalog.badges[badgeSlug]
  return formatBadgeDescription(t, badgeSlug, catalog?.description || "")
}

export function buildImpactedBenefits(
  t: TranslateFn,
  badge: BadgeState | null | undefined,
  progressData: UserProgressResponse | null
): ImpactedBenefitView[] {
  const benefitMap = new Map((progressData?.benefits || []).map((benefit) => [benefit.slug, benefit]))
  return (badge?.benefits || []).map((slug) => {
    const state = benefitMap.get(slug) as BenefitState | undefined
    return {
      slug,
      title: formatBenefitName(t, slug),
      progress: state?.progress ?? 0,
      unlocked: state?.unlocked ?? false,
      icon: BENEFIT_ICONS[slug] || "mdi-medal",
      statusLabel: benefitStatusLabel(t, state?.unlocked ?? false, state?.progress ?? 0),
    }
  })
}

export function buildConnexionActionDescription(t: TranslateFn, action: BadgeActionLike): string {
  const base = actionDescription(t, action.key)
  if (action.key !== "connexion.login") {
    return base
  }
  const template = t("$vuetify.progression_connexion_action_progress")
  const progress = template
    .replace("{done}", String(action.occurrence_count ?? 0))
    .replace("{total}", String(action.max_occurrences ?? 100))
  return base ? `${base} ${progress}` : progress
}

export function buildChecklistActions(
  t: TranslateFn,
  badge: BadgeState | null | undefined,
  options: { enrichConnexion?: boolean } = {}
): ChecklistAction[] {
  return (badge?.actions || []).map((action) => ({
    key: action.key,
    label: readableAction(t, action.key),
    description: options.enrichConnexion
      ? buildConnexionActionDescription(t, action)
      : actionDescription(t, action.key),
    completed: action.completed,
  }))
}
